import { action, computed, makeAutoObservable, observable } from "mobx";

export interface BidEntry {
  slots: number;
  status: boolean;
  deposit: number;
  stake: number;
}

export interface AuctionInfo {
  finalPrice: number;
  lockupEnd: number;
  slotsSold: number;
  start: number;  // block height of start
  end: number;  // block height of end that was set initially, *not* the block when finaliseAuction was called
  startStake: number;  // fet + 18 zeros
  reserveStake: number;
  declinePerBlock: number;
  slotsOnSale: number;
}

export class BidStore {
  @observable private data = new Map<string, BidEntry>();
  @observable private availableSlots = 0;
  @observable private totalSlots = 0;
  @observable private biddingPhase = true;
  @observable private finalized = false;
  @observable private cleared = false;
  @observable private selfDeposit = 0;
  @observable private selfStakeNeeded = 0;

  @observable private currentAuctionInfo: AuctionInfo = {
    declinePerBlock: 0,
    end: 0,
    finalPrice: 0,
    lockupEnd: 0,
    reserveStake: 0,
    slotsOnSale: 0,
    slotsSold: 0,
    start: 0,
    startStake: 0
  };

  constructor() {
    makeAutoObservable(this);
  }

  @action
  clear() {
    this.data.clear();
  }

  @action
  updateSelfDepositAndStake(deposit: number, stakeNeeded: number) {
    this.selfDeposit = deposit;
    this.selfStakeNeeded = stakeNeeded;
  }

  @action
  update(address: string, slots: number, status: boolean, deposit: number, stake = 0) {
    this.data.set(address, {
      slots: slots,
      status: status,
      deposit: deposit,
      stake: stake
    });
  }

  @action
  updateBiddingPhase(biddingPhase: boolean) {
    this.biddingPhase = biddingPhase;
  }

  @action
  updateFinalized(finalized: boolean) {
    this.finalized = finalized;
  }

  @action
  updateCleared(cleared: boolean) {
    this.cleared = cleared;
  }

  @action
  setAuctionInfo(auctionInfo: AuctionInfo) {
    this.currentAuctionInfo = auctionInfo;
  }

  @computed
  getAuctionInfo() {
    return this.currentAuctionInfo;
  }

  @computed
  isBiddingPhase() {
    return this.biddingPhase;
  }

  @computed
  isFinalized() {
    return this.finalized;
  }

  @computed
  isCleared() {
    return this.cleared;
  }

  @computed
  didBid(address: string) {
    return this.data.get(address) !== undefined;
  }

  @action
  updateSlots(available: number, total: number) {
    this.availableSlots = available;
    this.totalSlots = total;
  }

  @computed
  size() {
    return this.data.size;
  }

  @computed
  getBids() {
    return this.data;
  }

  @computed
  getAvailableSlots() {
    return this.availableSlots;
  }

  @computed
  getTotalSlots() {
    return this.totalSlots;
  }

  @computed
  getSelfDepositAndStake() {
    return [this.selfDeposit, this.selfStakeNeeded];
  }
}
